import {
  AHInput,
  AHText,
  AHTextFonts,
  Box,
  Card,
  TodoCategoryIconType,
  useLayoutContext,
} from "arkh";
import { FormattedMessage, useIntl } from "react-intl";
import React, { useCallback, useState } from "react";
import { IconBar } from "./IconBar";

import { useIntlTokens } from "../../components/mobile/useIntlTokens";
import { TipArea } from "../../components";

import { graphql, useMutation } from "react-relay";
import { TodoCategoryCreateScreenMutation } from "./__generated__/TodoCategoryCreateScreenMutation.graphql";
import { StoreAdminNavigationScreenProps } from "../StoreAdmin/StoreAdminNavigation";
import { useNavigationHeader } from "@/src/common/navigationHeaderOptions";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";

type TodoCategoryCreateScreenProps =
  StoreAdminNavigationScreenProps<"TodoCategoryCreate">;

export function TodoCategoryCreateScreen({
  navigation,
}: TodoCategoryCreateScreenProps) {
  const intl_tokens = useIntlTokens();

  const { space } = useLayoutContext();

  const [icon, setIcon] = useState<TodoCategoryIconType>("Check");
  const [title, setTitle] = useState("");

  const [create, loading] = useMutation<TodoCategoryCreateScreenMutation>(
    graphql`
      mutation TodoCategoryCreateScreenMutation(
        $title: String!
        $icon: TodoCategoryIcon!
      ) {
        todoCategoryCreate(icon: $icon, title: $title) {
          id
          title
          icon
        }
      }
    `
  );

  const onCreate = useCallback(() => {
    create({
      variables: {
        title,
        icon,
      },
      onCompleted: navigation.goBack,
    });
  }, [create, icon, navigation.goBack, title]);

  useNavigationHeader({
    type: "CREATE",
    onCancel: navigation.goBack,
    onSave: onCreate,
    saveLoading: loading,
    disabled: title.length < 1,
    createButtonLabel: intl_tokens.Create,
    title: intl_tokens.Create,
    defaultContentStyle: true,
  });
  const intl = useIntl();
  return (
    <KeyboardAwareScrollView
      contentInsetAdjustmentBehavior="automatic"
      style={{ flex: 1 }}
      keyboardShouldPersistTaps="handled"
      contentContainerStyle={{
        padding: space.medium,
        paddingVertical: space.medium,
      }}
    >
      <Box spaceBottom="medium">
        <TipArea>
          <AHText style={{ flex: 1 }} color="darkerText" type="body">
            <AHText font={AHTextFonts.BOLD} color="darkerText" type="body">
              {intl_tokens.TipTitle}
            </AHText>{" "}
            <FormattedMessage
              defaultMessage={`You can think of these as sorted mailboxes with custom labels for different types of to-dos. For example: "Customer Request", "To Do" or "Improvement Proposal".`}
              description="TodoCategoryCreateScreen tip"
            />
          </AHText>
        </TipArea>
      </Box>
      <Box spaceVertical="small">
        <AHText spaceBottom="small" spaceHorizontal="xsmall" type="title-small">
          <FormattedMessage
            defaultMessage="Name"
            description="StoreAdminTodoCategoriesCreate: Name Header"
          />
        </AHText>
        <AHInput
          autoCapitalize="words"
          defaultStyle
          placeholder={intl.formatMessage({
            defaultMessage: "Category name",
            description: "StoreAdminTodoCategoriesCreate: Name placeholder",
          })}
          value={title}
          onChangeText={setTitle}
          autoFocus
        />
      </Box>
      <Box spaceVertical="small" spaceBottom="xxlarge">
        <AHText spaceBottom="small" type="title-small" spaceHorizontal="xsmall">
          <FormattedMessage
            defaultMessage="Icon"
            description="StoreAdminTodoCategoriesCreate: Icon Header"
          />
        </AHText>
        <Card defaultBorder>
          <IconBar selectedIcon={icon} setSelectedIcon={setIcon} />
        </Card>
      </Box>
    </KeyboardAwareScrollView>
  );
}
